import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'

import { sanitizeImageUrl } from '../../helpers/sanitizeUrl.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useAuth } from '../../hooks/useAuth'
import { useImagenesPublicacion } from '../../hooks/useImagenesPublicacion.js'
import { useGlobalFileDrag } from '../hooks/useGlobalFileDrag'
import CarouselBig from '../publicaciones/carouselBig/CarouselBig.jsx'
import * as archivoAdjuntoService from '../servicios/archivoAdjuntoService'
import * as imagenesService from '../servicios/imagenesService'
import LinkButton from '../ui/LinkButton'
import styles from './Upload.module.css'

const MAX_FILES = 5
const MAX_FILE_CARGA_MASIVA = 30

const Upload = ({ idPublicacion, idPublicacionTemporal, accept, cargaMasiva }) => {
  const { isDraggingFile } = useGlobalFileDrag()

  const [reload, setReload] = useState(true)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [isOverDropZone, setIsOverDropZone] = useState(false)
  const [show, setShow] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const { loginDataContext: { idUsuario } = {} } = useAuth()

  const { adjuntos, setAdjuntos, fetchImagenes } = useImagenesPublicacion()

  useEffect(() => {
    const faltaUsuarioEnCargaMasiva = cargaMasiva && !idUsuario
    if (
      (idPublicacion > 0 || idPublicacionTemporal > 0 || cargaMasiva) &&
      reload &&
      !faltaUsuarioEnCargaMasiva
    ) {
      fetchImagenes({
        idPublicaciones: idPublicacion > 0 ? [idPublicacion] : [],
        idPublicacionTemporal,
        idUsuario,
        tieneFotoDefault: false,
        soloImagenPrincipal: false
      })
      setReload(false)
    }
  }, [idPublicacion, idPublicacionTemporal, reload, cargaMasiva, idUsuario])

  const onDrop = (acceptedFiles) => {
    const maxFiles = cargaMasiva ? MAX_FILE_CARGA_MASIVA : MAX_FILES
    if (adjuntos.length >= maxFiles) {
      toast.warn(`Solo se permiten ${maxFiles} archivos.`)
      return
    }

    const filesToUpload = acceptedFiles.slice(0, maxFiles - adjuntos.length)

    filesToUpload.forEach((archivo, index) => {
      const fileNameWithoutExt = archivo.name.replace(/\.[^/.]+$/, '')

      if (
        adjuntos.find((a) => {
          const adjuntoNameWithoutExt = a.nombre.replace(/\.[^/.]+$/, '')
          return (
            adjuntoNameWithoutExt === fileNameWithoutExt ||
            (a.url && a.url.includes(fileNameWithoutExt))
          )
        })
      ) {
        toast.warn('El archivo ya existe en la galería.')
        return
      }

      upload(idPublicacion, idPublicacionTemporal, archivo, adjuntos.length + index + 1)
    })

    if (acceptedFiles.length > filesToUpload.length) {
      toast.warn(`Máximo ${maxFiles} archivos permitidos.`)
    }

    setIsOverDropZone(false)
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDragOver: () => setIsOverDropZone(true),
    onDragLeave: () => setIsOverDropZone(false),
    accept,
    noClick: true,
    noKeyboard: true
  })

  const upload = async (idPublicacion, idPublicacionTemporal, archivo, orden) => {
    try {
      const datosForm = new FormData()
      datosForm.append('nombre', archivo.name)
      if (idPublicacion > 0) {
        datosForm.append('idPublicacion', idPublicacion)
      }
      if (idPublicacionTemporal > 0) {
        datosForm.append('idPublicacionTemporal', idPublicacionTemporal)
      }
      datosForm.append('registroAddUsr', idUsuario)
      datosForm.append('archivo', archivo)
      datosForm.append('orden', orden)

      await imagenesService.subirImagenes(datosForm)
      setReload(true)
    } catch (error) {
      const mensaje = `Ocurrió un error al subir el archivo.`
      toastErrorLog(error, mensaje, 'adjunto')
    }
  }

  const borrarArchivo = async (e, idAdjunto) => {
    try {
      e.stopPropagation()
      const adjuntoEliminar = adjuntos.find((a) => a.idAdjunto === idAdjunto)
      if (adjuntoEliminar) {
        const ordenEliminado = adjuntoEliminar.orden
        const nuevosAdjuntos = adjuntos
          .filter((a) => a.idAdjunto !== idAdjunto)
          .map((a) => (a.orden > ordenEliminado ? { ...a, orden: a.orden - 1 } : a))
        setAdjuntos(nuevosAdjuntos)
        await archivoAdjuntoService.modificarOrdenImagenes(nuevosAdjuntos)
      }
      await imagenesService.borrarImagen(idAdjunto)
      setReload(true)
    } catch (error) {
      const mensaje = `Ocurrió un error al borrar el archivo.`
      toastErrorLog(error, mensaje, 'adjunto')
    }
  }

  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDropGallery = async (index, e) => {
    e.preventDefault()
    if (draggedIndex === null) {
      return
    }

    const newAdjuntos = [...adjuntos]
    const [moved] = newAdjuntos.splice(draggedIndex, 1)
    newAdjuntos.splice(index, 0, moved)

    const updatedAdjuntos = newAdjuntos.map((a, idx) => ({
      ...a,
      orden: idx + 1
    }))
    setAdjuntos(updatedAdjuntos)
    setDraggedIndex(null)

    await archivoAdjuntoService.modificarOrdenImagenes(updatedAdjuntos)
  }

  const mostrarImagenGrande = (index) => {
    setSelectedIndex(index)
    setShow(true)
  }

  return (
    <>
      <div
        {...getRootProps()}
        className={`${styles.uploadZone} ${isOverDropZone ? styles.active : ''}`}
        onClick={open}
      >
        <i className="fa fa-camera fa-2x" aria-hidden="true" />
        <input {...getInputProps()} />
        <p>{isDraggingFile ? '📸 Arrastralas acá...' : 'Agregalas desde tu computadora'}</p>
      </div>

      <div className={styles.gallery}>
        {adjuntos.map((a, index) => (
          <div
            key={a.idAdjunto}
            className={styles.imageCol}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDropGallery(index, e)}
            onClick={() => mostrarImagenGrande(index)}
          >
            <div className={styles.imageWrapper}>
              <LinkButton
                onClick={(e) => borrarArchivo(e, a.idAdjunto)}
                className={styles.deleteBtn}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </LinkButton>
              <img
                alt={`img-${index}`}
                src={sanitizeImageUrl(a.url)}
                className={styles.uploadedImg}
              />
            </div>
            {cargaMasiva && <p className={styles.fileID}>ID: {a.idAdjunto}</p>}
          </div>
        ))}
      </div>
      <Modal show={show} onHide={() => setShow(false)} size="lg" centered id="modalCarousel">
        <Modal.Header closeButton>
          <Modal.Title></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CarouselBig adjuntos={adjuntos} initialSlide={selectedIndex} />
        </Modal.Body>
      </Modal>
    </>
  )
}

Upload.propTypes = {
  idPublicacion: PropTypes.number,
  idPublicacionTemporal: PropTypes.number,
  accept: PropTypes.object.isRequired,
  cargaMasiva: PropTypes.bool
}

export default Upload
